import * as ADDTL_INCOME_TYPES from "../constants/additional_income_types";
import * as MARITAL_STATUS_TYPES from "../constants/marital_status";
import * as OWNERSHIP_TYPES from "../constants/ownership_options";
import * as CREDIT_SCORE_RANGES from "../constants/credit_score";
import * as PROPERTY_CLASSIFICATION from "../constants/property_classification";
import * as PROPERTY_TYPE from "../constants/property_type";
import * as LOAN_TYPES from "../constants/loan_types";

/**
 * An object mapping the person name key from Lanis borrowers to the name of
 * the tag present in the MISMO XML file for the individual name child tags.
 * For example, `borrower.first_name = "John";` maps to a tag
 * `<INDIVIDUAL><NAME><FirstName>John</FirstName></NAME></INDIVIDUAL>`.
 */
export const INDIVIDUAL_NAME_PROPS = Object.freeze({
  first_name: "FirstName",
  last_name: "LastName",
  middle_name: "MiddleName",
  suffix: "SuffixName"
});

export const ADDRESS_PROPS = Object.freeze({
  current_address: "AddressLineText",
  current_address_2: "AddressAdditionalLineText",
  city: "CityName",
  zip_code: "PostalCode"
});

export const ADDRESS_STATE_PROPS = Object.freeze({
  state_name: "StateName",
  state_code: "StateCode"
});

/**
 * Maps the additional income type IDs from Lanis to the corresponding MISMO
 * IncomeEnum values.
 */
export const INCOME_TYPE_ENUM = Object.freeze({
  [ADDTL_INCOME_TYPES.ALIMONY]: "Alimony",
  [ADDTL_INCOME_TYPES.AUTOMOBILE_ALLOWANCE]: "AutomobileAllowance",
  [ADDTL_INCOME_TYPES.BOARDER_INCOME]: "BoarderIncome",
  [ADDTL_INCOME_TYPES.CAPITAL_GAINS]: "CapitalGains",
  [ADDTL_INCOME_TYPES.CHILD_SUPPORT]: "ChildSupport",
  [ADDTL_INCOME_TYPES.DISABILITY]: "Disability",
  [ADDTL_INCOME_TYPES.EMPLOYMENT_RELATED_ASSETS]: "EmploymentRelatedAccount", // TODO right?
  [ADDTL_INCOME_TYPES.FOREIGN_INCOME]: "", // TODO ?
  [ADDTL_INCOME_TYPES.FOSTER_CARE]: "FosterCare",
  [ADDTL_INCOME_TYPES.HOUSING_OR_PARSONAGE]: "", // TODO ?
  [ADDTL_INCOME_TYPES.INTEREST_AND_DIVIDENDS]: "DividendsInterest",
  [ADDTL_INCOME_TYPES.MILITARY_BASE_PAY]: "MilitaryBasePay",
  [ADDTL_INCOME_TYPES.MILITARY_CLOTHES_ALLOWANCE]: "MilitaryClothesAllowance",
  [ADDTL_INCOME_TYPES.MILITARY_COMBAT_PAY]: "MilitaryCombatPay",
  [ADDTL_INCOME_TYPES.MILITARY_FLIGHT_PAY]: "MilitaryFlightPay",
  [ADDTL_INCOME_TYPES.MILITARY_HAZARD_PAY]: "MilitaryHazardPay",
  [ADDTL_INCOME_TYPES.MILITARY_OVERSEAS_PAY]: "MilitaryOverseasPay",
  [ADDTL_INCOME_TYPES.MILITARY_PROP_PAY]: "MilitaryPropPay",
  [ADDTL_INCOME_TYPES.MILITARY_QUARTERS_ALLOWANCE]: "MilitaryQuartersAllowance",
  [ADDTL_INCOME_TYPES.MILITARY_RATIONS_ALLOWANCE]: "MilitaryRationsAllowance",
  [ADDTL_INCOME_TYPES.MILITARY_VARIABLE_HOUSING_ALLOWANCE]: "MilitaryVariableHousingAllowance",
  [ADDTL_INCOME_TYPES.MORTGAGE_CREDIT_CERTIFICATE]: "MortgageCreditCertificate",
  [ADDTL_INCOME_TYPES.MORTGAGE_DIFFERENTIAL_PAYMENTS]: "MortgageDifferential", // TODO right?
  [ADDTL_INCOME_TYPES.NOTES_RECEIVABLE]: "NotesReceivableInstallment", // TODO right?
  [ADDTL_INCOME_TYPES.OTHER_INCOME]: "Other",
  [ADDTL_INCOME_TYPES.PUBLIC_ASSISTANCE]: "PublicAssistance",
  [ADDTL_INCOME_TYPES.REAL_ESTATE_MORTGAGE_DIFFERENTIAL_INCOME]: "RealEstateOwnedGrossRentalIncome", // TODO right?
  [ADDTL_INCOME_TYPES.RETIREMENT]: "Pension", // TODO right?
  [ADDTL_INCOME_TYPES.ROYALTY_PAYMENTS]: "Royalties",
  [ADDTL_INCOME_TYPES.SEASONAL_INCOME]: "", // TODO ?
  [ADDTL_INCOME_TYPES.SEPARATE_MAINTENANCE]: "SeparateMaintenance",
  [ADDTL_INCOME_TYPES.SOCIAL_SECURITY]: "SocialSecurity",
  [ADDTL_INCOME_TYPES.SUBJECT_PROPERTY_NET_CASH_FLOW]: "SubjectPropertyNetCashFlow",
  [ADDTL_INCOME_TYPES.TEMPORARY_LEAVE]: "TemporaryLeave",
  [ADDTL_INCOME_TYPES.TIP_INCOME]: "TipIncome",
  [ADDTL_INCOME_TYPES.TRAILING_CO_BORROWER_INCOME]: "TrailingCoBorrowerIncome",
  [ADDTL_INCOME_TYPES.TRUST_INCOME]: "Trust",
  [ADDTL_INCOME_TYPES.UNEMPLOYMENT]: "Unemployment",
  [ADDTL_INCOME_TYPES.VA_BENEFITS]: "VABenefitsNonEducational",
  // Added 2026-08-01 (POINT "Income from Other Sources" parity). Types with no
  // MISMO enumeration value are left "" and export as "Other" with a
  // description (the falsy check in populateIncomeItemDetail), which also keeps
  // them from shadowing the real OTHER_INCOME on import.
  [ADDTL_INCOME_TYPES.ACCESSORY_UNIT_INCOME]: "AccessoryUnitIncome",
  [ADDTL_INCOME_TYPES.DEFINED_CONTRIBUTION_PLAN]: "DefinedContributionPlan",
  [ADDTL_INCOME_TYPES.HOUSING_ALLOWANCE]: "HousingAllowance",
  [ADDTL_INCOME_TYPES.HOUSING_CHOICE_VOUCHER]: "HousingChoiceVoucherProgram",
  [ADDTL_INCOME_TYPES.NET_RENTAL_INCOME]: "NetRentalIncome",
  [ADDTL_INCOME_TYPES.NON_BORROWER_CONTRIBUTION]: "NonBorrowerContribution",
  [ADDTL_INCOME_TYPES.PENSION]: "Pension",
  [ADDTL_INCOME_TYPES.RESTRICTED_STOCK]: "",
  // VA Compensation shares MISMO's only VA bucket (VABenefitsNonEducational)
  // with VA Non-Educational Benefits; both export to it. On import that value
  // resolves to VA Non-Educational Benefits (lower id) - acceptable, same bucket.
  [ADDTL_INCOME_TYPES.VA_COMPENSATION]: "VABenefitsNonEducational"
});

export const MARITAL_STATUS_ENUM = Object.freeze({
  [MARITAL_STATUS_TYPES.MARRIED]: "Married",
  [MARITAL_STATUS_TYPES.SEPARATED]: "Separated",
  [MARITAL_STATUS_TYPES.UNMARRIED]: "Unmarried"
});

export const OWNERSHIP_ENUM = Object.freeze({
  [OWNERSHIP_TYPES.OWNERSHIP_LESS_THAN_25]: "LessThan25Percent",
  [OWNERSHIP_TYPES.OWNERSHIP_OVER_25]: "GreaterThanOrEqualTo25Percent"
});

/**
 * Maps Lanis credit score range IDs to the minimum value that exists in that
 * range. For example, `RANGE_720` represents a credit score of 720-739 but
 * this maps that range to 720.
 */
export const CREDIT_SCORE_VALUES = Object.freeze({
  [CREDIT_SCORE_RANGES.RANGE_800]: 800,
  [CREDIT_SCORE_RANGES.RANGE_760]: 760,
  [CREDIT_SCORE_RANGES.RANGE_740]: 740,
  [CREDIT_SCORE_RANGES.RANGE_720]: 720,
  [CREDIT_SCORE_RANGES.RANGE_700]: 700,
  [CREDIT_SCORE_RANGES.RANGE_680]: 680,
  [CREDIT_SCORE_RANGES.RANGE_660]: 660,
  [CREDIT_SCORE_RANGES.RANGE_640]: 640,
  [CREDIT_SCORE_RANGES.RANGE_620]: 620,
  [CREDIT_SCORE_RANGES.RANGE_600]: 600,
  [CREDIT_SCORE_RANGES.RANGE_580]: 580,
  [CREDIT_SCORE_RANGES.RANGE_560]: 560
});

export const PROPERTY_USAGE_TYPE_ENUM = Object.freeze({
  [PROPERTY_CLASSIFICATION.INVESTMENT_PROPERTY]: "Investment",
  [PROPERTY_CLASSIFICATION.PRIMARY_RESIDENCE]: "PrimaryResidence",
  [PROPERTY_CLASSIFICATION.SECOND_HOME]: "SecondHome",
  [PROPERTY_CLASSIFICATION.VACATION_HOME]: "SecondHome"
});

export const UNIT_COUNT_MAP = Object.freeze({
  [PROPERTY_TYPE.DUPLEX_1_2]: 2,
  [PROPERTY_TYPE.MULTI_UNIT_3_4]: 4
});

export const GSE_PROPERTY_TYPE_ENUM = Object.freeze({
  [PROPERTY_TYPE.ATTACHED]: "Attached",
  [PROPERTY_TYPE.CONDOMINIUM]: "Condominium",
  [PROPERTY_TYPE.COOPERATIVE]: "Cooperative",
  [PROPERTY_TYPE.DETACHED]: "Detached",
  [PROPERTY_TYPE.DETACHED_CONDOMINIUM]: "DetachedCondominium",
  [PROPERTY_TYPE.DUPLEX_1_2]: "2-4 Unit",
  [PROPERTY_TYPE.HIGH_RISE_CONDOMINIUM]: "HighRiseCondominium",
  [PROPERTY_TYPE.MANUFACTURED_SINGLE_WIDE]: "ManufacturedHousing",
  [PROPERTY_TYPE.MANUFACTURED_DOUBLE_WIDE]: "ManufacturedHousing",
  [PROPERTY_TYPE.MANUFACTURED_MULTI_WIDE]: "ManufacturedHousing",
  [PROPERTY_TYPE.MANUFACTURED_CONDO_PUD_COOP]: "ManufacturedHousingCondominiumPUDCoOp",
  [PROPERTY_TYPE.MODULAR_PRE_FABRICATED_HOME]: "ManufacturedHousing",
  [PROPERTY_TYPE.MULTI_UNIT_3_4]: "2-4 Unit",
  [PROPERTY_TYPE.PUD]: "PlannedUnitDevelopment"
});

// LANIS property type -> GSEPropertyType. POINT reads its Property Type dropdown
// from GSEPropertyType (a direct child of PROPERTY_DETAIL); without it the field
// stays blank. Reverse of GSE_PROPERTY_TYPE_TO_LANIS.
export const LANIS_TO_GSE_PROPERTY_TYPE = Object.freeze({
  [PROPERTY_TYPE.ATTACHED]: "Attached",
  [PROPERTY_TYPE.CONDOMINIUM]: "Condominium",
  [PROPERTY_TYPE.COOPERATIVE]: "Cooperative",
  [PROPERTY_TYPE.DETACHED]: "Detached",
  [PROPERTY_TYPE.DETACHED_CONDOMINIUM]: "DetachedCondominium",
  [PROPERTY_TYPE.MULTI_UNIT_3_4]: "2-4 Unit",
  [PROPERTY_TYPE.HIGH_RISE_CONDOMINIUM]: "HighRiseCondominium",
  [PROPERTY_TYPE.MANUFACTURED_SINGLE_WIDE]: "ManufacturedHousing",
  [PROPERTY_TYPE.MANUFACTURED_CONDO_PUD_COOP]: "ManufacturedHousingCondominiumPUDCoOp",
  [PROPERTY_TYPE.PUD]: "PlannedUnitDevelopment"
});

export const GSE_PROPERTY_TYPE_TO_LANIS = Object.freeze({
  "Attached": PROPERTY_TYPE.ATTACHED,
  "Condominium": PROPERTY_TYPE.CONDOMINIUM,
  "Cooperative": PROPERTY_TYPE.COOPERATIVE,
  "Detached": PROPERTY_TYPE.DETACHED,
  "DetachedCondominium": PROPERTY_TYPE.DETACHED_CONDOMINIUM,
  "2-4 Unit": PROPERTY_TYPE.MULTI_UNIT_3_4,
  "HighRiseCondominium": PROPERTY_TYPE.HIGH_RISE_CONDOMINIUM,
  "ManufacturedHousing": PROPERTY_TYPE.MANUFACTURED_SINGLE_WIDE,
  "ManufacturedHousingCondominiumPUDCoOp": PROPERTY_TYPE.MANUFACTURED_CONDO_PUD_COOP,
  "PlannedUnitDevelopment": PROPERTY_TYPE.PUD
});

export const MORTGAGE_TYPE_ENUM = Object.freeze({
  [LOAN_TYPES.FANNIEMAE]: "Conventional",
  [LOAN_TYPES.FREDDIEMAC]: "Conventional",
  [LOAN_TYPES.JUMBO]: "Conventional",
  [LOAN_TYPES.PORTFOLIO]: "Other",
  [LOAN_TYPES.FHA_PURCHASE]: "FHA",
  [LOAN_TYPES.HARD_MONEY]: "Other",
  [LOAN_TYPES.USDA_PURCHASE]: "USDARuralDevelopment",
  [LOAN_TYPES.VA_PURCHASE]: "VA",
  [LOAN_TYPES.OTHER]: "Other",
  [LOAN_TYPES.CONSTRUCTION]: "Other",
  [LOAN_TYPES.NON_QM]: "Other",
  [LOAN_TYPES.FIX_AND_FLIP]: "Other",
  [LOAN_TYPES.FHA_STREAMLINE]: "FHA",
  [LOAN_TYPES.VA_IRRRL]: "VA"
});
